import React, {useEffect, useState} from "react";
import { useAuthContext } from "../hooks/useContext/useAuthContext";
import { useResetPassword } from "../hooks/useResetPassword";
import { useResetEmail } from "../hooks/useResetEmail";
import Footer from "../components/Shared/Footer";
import Navbar from "../components/NavBar/Navbar";
import ErrorBox from "../components/Shared/ErrorBox";
import { Box, Button, Typography, CircularProgress, Divider, Grid } from '@mui/material';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '../hooks/useTranslation';

const GridContainerMain = styled(Grid)(({ theme }) => ({
    backgroundColor: theme.palette.common.background,
    minHeight: "100vh",
    paddingLeft:'10%',
    paddingRight: '10%',
    paddingTop: '50px',
    display: 'flex',
    justifyContent: 'center',
  }));

const Account = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const {resetPassword, error} = useResetPassword();
  const {resetEmail, emailError} = useResetEmail();
  const [verificationMessage, setVerificationMessage] = useState("");
  const [emailVerificationMessage, setEmailVerificationMessage] = useState("");
  const [isLoadingReset, setIsLoadingReset] = useState(false);
  const [isEmailLoadingReset, setIsEmailLoadingReset] = useState(false);

  if (!user) {
    window.location.href = "/login";
  }

  const changeEmailLink = async () => {
    setIsEmailLoadingReset(true);
    const email = user.email;
    await resetEmail(email, emailError);
    setIsEmailLoadingReset(false);
  };

  const sendPasswordLink = async () => {
    setIsLoadingReset(true);
    const email = user.email;
    await resetPassword(email, error);
    setIsLoadingReset(false);
  };

  useEffect(() => {
    if (emailError === "We have emailed you a link to change your email") {
      setEmailVerificationMessage(t('emailedLinkChangeEmail'));
    } else if (emailError) {
      setEmailVerificationMessage(t('anErrorOccurred'));
    } else {
      setEmailVerificationMessage("");
    }
  }, [emailError, t]);

  useEffect(() => {
    if (error === "We have emailed you a link to reset your password") {
      setVerificationMessage(t('emailedLinkResetPassword'));
    } else if (error) {
      setVerificationMessage(t('anErrorOccurred'));
    } else {
      setVerificationMessage("");
    }
  }, [error, t]);


  return (
    <div style={{backgroundColor: theme.palette.common.background}}>
      <Navbar />
      <GridContainerMain container spacing={0}>
        <Grid item xs={12} sm={11} md={10} lg={9} xl={9}>
            <Typography variant="h1" component="h1" paddingBottom="50px">
              {t('accountSettings')}
            </Typography>
            <Box sx={{ border: '1px solid #becde2', padding:'40px', marginBottom:'50px', borderRadius: '10px', backgroundColor: theme.palette.component.componentBackground }}>
                <Typography variant="h6" component="p" paddingBottom="15px">
                    {t('account')}
                </Typography>

                <Divider color='#becde2'/>

                <Box sx={{ display: 'flex', alignItems: 'center', paddingTop: '50px', paddingBottom: '50px' }}>
                    <Typography sx={{paddingRight: {xs: "20px", sm: "30px", md: "40px", lg: "50px", xl: "50px"}}} component="p" color='#8f8f8f'>
                        {t('email')}
                    </Typography>
                    <Typography paddingRight="50px" component="p" color='#000000'>
                        {user.email}
                    </Typography>
                </Box>
                {isEmailLoadingReset ? (<CircularProgress />) : (
                <Button
                    variant="outlined"
                    onClick={changeEmailLink}
                    sx={{
                        borderRadius: '30px',
                        color: theme.palette.common.white,
                        backgroundColor: theme.palette.primary.main,
                        ":hover": { bgcolor: theme.palette.secondary.main},
                        textTransform: 'none',
                    }}
                >
                    {t('updateEmail')}
                </Button>)}

                {emailError && (
                  <ErrorBox error={emailError} color={emailVerificationMessage === t('emailedLinkChangeEmail') ? 'success' : 'error'} />
                )}

            </Box>
            <Box sx={{ border: '1px solid #becde2', padding:'40px', marginBottom:'50px', borderRadius: '10px', backgroundColor: theme.palette.component.componentBackground }}>
                <Typography variant="h6" component="p" paddingBottom="15px">
                  {t('security')}
                </Typography>

                <Divider color='#becde2'/>

                <Box sx={{ display: 'flex', alignItems: 'center', paddingTop: '50px', paddingBottom: '50px' }}>
                    <Typography component="p" color='#8f8f8f' paddingRight="50px">
                        {t('password')}
                    </Typography>
                </Box>
                {isLoadingReset ? (<CircularProgress />) : (
                <Button
                    variant="outlined"
                    onClick={sendPasswordLink}
                    sx={{
                        borderRadius: '30px',
                        color: theme.palette.common.white,
                        backgroundColor: theme.palette.primary.main,
                        ":hover": { bgcolor: theme.palette.secondary.main},
                        textTransform: 'none',
                    }}
                >
                {t('updatePassword')}
                </Button>)}
                {error && (
                  <ErrorBox error={error} color={ verificationMessage === t('emailedLinkResetPassword') ? 'success' : 'error' } />
                )}
            </Box>
        </Grid>
      </GridContainerMain>
      <Footer />
    </div>
  );
};

export default Account;